import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { VersionNodeProps, DisplayMode } from "./types";
import { ProjectVersion } from "@/stores/versioning/versioning-store";
import { VersionActionButtons } from "./VersionActionButtons";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Clock,
  User,
  Tag,
  FileText,
  ChevronDown,
  ChevronUp,
  GitCommit,
  Eye,
  Edit,
  CheckCircle,
  Circle,
  MoreHorizontal,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const getVersionStatusColor = (version: ProjectVersion, isCurrent: boolean) => {
  if (isCurrent) return "bg-green-500";
  if (version.isPublished) return "bg-blue-500";
  return "bg-orange-500";
};

const getVersionStatusLabel = (version: ProjectVersion, isCurrent: boolean) => {
  if (isCurrent) return "Current";
  if (version.isPublished) return "Published";
  return "Draft";
};

const getUserInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const formatChangeCount = (changes: any[]): string => {
  const count = changes?.length || 0;
  if (count === 0) return "No changes";
  if (count === 1) return "1 change";
  return `${count} changes`;
};

export const VersionNode: React.FC<VersionNodeProps> = ({
  version,
  isSelected,
  isCurrent,
  isCompareBase,
  isCompareTarget,
  showActions,
  displayMode,
  onSelect,
  onRestore,
  onCompare,
  onDelete,
  onPublish,
  onViewDetails,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const statusColor = getVersionStatusColor(version, isCurrent);
  const statusLabel = getVersionStatusLabel(version, isCurrent);
  const userInitials = getUserInitials(version.createdBy);
  const changeCount = formatChangeCount(version.changes);
  const timeAgo = formatDistanceToNow(version.createdAt, { addSuffix: true });

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        onSelect();
        break;
      case "r":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          onRestore?.();
        }
        break;
      case "c":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          onCompare?.();
        }
        break;
      case "d":
        if (showDetails !== undefined) {
          setShowDetails(!showDetails);
        } else {
          onViewDetails?.();
        }
        break;
    }
  };

  const renderCompactView = () => (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md",
        isSelected && "ring-2 ring-primary shadow-md",
        isCurrent && "border-green-500",
        isCompareBase && "ring-2 ring-blue-500",
        isCompareTarget && "ring-2 ring-purple-500",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Version ${version.version} - ${statusLabel} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            {/* Version indicator */}
            <div className="flex items-center space-x-2">
              <div className={cn("w-3 h-3 rounded-full", statusColor)} />
              <span className="font-mono text-sm font-semibold">
                v{version.version}
              </span>
            </div>

            {/* Version info */}
            <div className="flex-1 min-w-0">
              {version.name && (
                <h4 className="font-medium text-sm truncate mb-1">
                  {version.name}
                </h4>
              )}

              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <User className="w-3 h-3" />
                  <span className="truncate">{version.createdBy}</span>
                </div>
                <span>•</span>
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{timeAgo}</span>
                </div>
                {version.changes && version.changes.length > 0 && (
                  <>
                    <span>•</span>
                    <span>{changeCount}</span>
                  </>
                )}
              </div>
            </div>

            {/* Status badge */}
            <Badge
              variant={
                isCurrent
                  ? "default"
                  : version.isPublished
                    ? "secondary"
                    : "outline"
              }
              className={cn(
                "text-xs",
                isCurrent && "bg-green-500 hover:bg-green-600",
              )}
            >
              {statusLabel}
            </Badge>
          </div>

          {/* Actions */}
          {showActions && (isHovered || isSelected) && (
            <div className="ml-3 flex-shrink-0">
              <VersionActionButtons
                version={version}
                isCurrent={isCurrent}
                onRestore={onRestore}
                onCompare={onCompare}
                onDelete={onDelete}
                onPublish={onPublish}
                onViewDetails={onViewDetails}
                size="sm"
                variant="inline"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderDetailedView = () => (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md",
        isSelected && "ring-2 ring-primary shadow-md",
        isCurrent && "border-green-500",
        isCompareBase && "ring-2 ring-blue-500",
        isCompareTarget && "ring-2 ring-purple-500",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Version ${version.version} - ${statusLabel} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Status indicator */}
            <div className={cn("w-4 h-4 rounded-full", statusColor)} />

            {/* Version number */}
            <span className="font-mono text-lg font-bold">
              v{version.version}
            </span>

            {/* Version title */}
            {version.name && (
              <h4 className="font-semibold text-base truncate">
                {version.name}
              </h4>
            )}
          </div>

          {/* Status badge */}
          <Badge
            variant={
              isCurrent
                ? "default"
                : version.isPublished
                  ? "secondary"
                  : "outline"
            }
            className={cn(isCurrent && "bg-green-500 hover:bg-green-600")}
          >
            {statusLabel}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Description */}
        {version.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {version.description}
          </p>
        )}

        {/* Metadata row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            {/* Author */}
            <div className="flex items-center space-x-2">
              <Avatar className="w-5 h-5">
                <AvatarFallback className="text-xs">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <span>{version.createdBy}</span>
            </div>

            {/* Time */}
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{timeAgo}</span>
            </div>

            {/* Changes count */}
            {version.changes && version.changes.length > 0 && (
              <div className="flex items-center space-x-1">
                <GitCommit className="w-4 h-4" />
                <span>{changeCount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        {version.tags && version.tags.length > 0 && (
          <div className="flex items-center space-x-2 mb-3">
            <Tag className="w-4 h-4 text-muted-foreground" />
            <div className="flex flex-wrap gap-1">
              {version.tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {version.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{version.tags.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Expandable details */}
        {version.changes && version.changes.length > 0 && (
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between p-0 h-auto"
              >
                <span className="text-xs text-muted-foreground">
                  View changes ({version.changes.length})
                </span>
                {showDetails ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-2">
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {version.changes.slice(0, 5).map((change, index) => (
                  <div
                    key={change.id || index}
                    className="flex items-center space-x-2 text-xs text-muted-foreground"
                  >
                    <div
                      className={cn(
                        "w-1.5 h-1.5 rounded-full",
                        change.type === "create" && "bg-green-500",
                        change.type === "update" && "bg-blue-500",
                        change.type === "delete" && "bg-red-500",
                        change.type === "move" && "bg-purple-500",
                      )}
                    />
                    <span className="capitalize">{change.type}</span>
                    <span>{change.target}</span>
                    <span className="truncate flex-1">
                      {change.description || `${change.target} ${change.type}d`}
                    </span>
                  </div>
                ))}
                {version.changes.length > 5 && (
                  <div className="text-xs text-muted-foreground text-center pt-1">
                    +{version.changes.length - 5} more changes
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex justify-end mt-3 pt-3 border-t">
            <VersionActionButtons
              version={version}
              isCurrent={isCurrent}
              onRestore={onRestore}
              onCompare={onCompare}
              onDelete={onDelete}
              onPublish={onPublish}
              onViewDetails={onViewDetails}
              size="sm"
              variant="inline"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderGridView = () => (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md h-full",
        isSelected && "ring-2 ring-primary shadow-md",
        isCurrent && "border-green-500",
        isCompareBase && "ring-2 ring-blue-500",
        isCompareTarget && "ring-2 ring-purple-500",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Version ${version.version} - ${statusLabel} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      <CardContent className="p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={cn("w-3 h-3 rounded-full", statusColor)} />
            <span className="font-mono font-bold">v{version.version}</span>
          </div>

          <Badge
            variant={
              isCurrent
                ? "default"
                : version.isPublished
                  ? "secondary"
                  : "outline"
            }
            className={cn(
              "text-xs",
              isCurrent && "bg-green-500 hover:bg-green-600",
            )}
          >
            {statusLabel}
          </Badge>
        </div>

        {/* Title */}
        {version.name && (
          <h4 className="font-medium text-sm mb-2 line-clamp-2 flex-shrink-0">
            {version.name}
          </h4>
        )}

        {/* Description */}
        {version.description && (
          <p className="text-xs text-muted-foreground mb-3 line-clamp-3 flex-1">
            {version.description}
          </p>
        )}

        {/* Metadata */}
        <div className="space-y-2 text-xs text-muted-foreground flex-shrink-0">
          <div className="flex items-center space-x-1">
            <User className="w-3 h-3" />
            <span className="truncate">{version.createdBy}</span>
          </div>

          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{timeAgo}</span>
          </div>

          {version.changes && version.changes.length > 0 && (
            <div className="flex items-center space-x-1">
              <GitCommit className="w-3 h-3" />
              <span>{changeCount}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {version.tags && version.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {version.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {version.tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{version.tags.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && (isHovered || isSelected) && (
          <div className="flex justify-center mt-3 pt-3 border-t">
            <VersionActionButtons
              version={version}
              isCurrent={isCurrent}
              onRestore={onRestore}
              onCompare={onCompare}
              onDelete={onDelete}
              onPublish={onPublish}
              onViewDetails={onViewDetails}
              size="sm"
              variant="dropdown"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render based on display mode
  switch (displayMode) {
    case "timeline":
      return renderCompactView();
    case "list":
      return renderDetailedView();
    case "grid":
      return renderGridView();
    default:
      return renderDetailedView();
  }
};

export default VersionNode;
